import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import { MarketingFooter } from '../components/layout/MarketingFooter';
import { Spinner } from '../components/Spinner';
import { API_BASE } from '../server/api';

const DOMAIN_MAX_LEN = 253;

const FRIENDLY_DOMAIN_ERROR = 'Enter a valid domain (e.g. yourbusiness.com)';

/**
 * Hostname shape validator — pure linear scan, no regex.
 *
 * Replaces the previous `DOMAIN_RE`, which had star-height 2
 * (`(?:\.[a-z]{2,})+` — outer `+` wrapping a group with a `{2,}`
 * inside) and was flagged by `security/detect-unsafe-regex`. Preserves
 * the same stricter-than-RFC public-signup semantics:
 *   - Main label: 2..63 chars, alphanumeric start/end, alphanumeric or
 *     hyphen in the middle.
 *   - At least one trailing dot-separated suffix segment.
 *   - Every suffix segment (TLD-like) is 2+ pure ASCII alpha.
 *
 * Input must already be lowercase and stripped of everything outside
 * `[a-z0-9.-]` — `sanitizeDomainInputClient` does both before calling
 * this. If that preprocessing ever changes, revisit the assumptions.
 * Cost is strictly O(s.length), bounded by DOMAIN_MAX_LEN.
 */
function isValidDomainShape(s: string): boolean {
  if (s.length < 3 || s.length > DOMAIN_MAX_LEN) return false;
  const firstDot = s.indexOf('.');
  // Main label must exist, be 2+ chars, and not be the entire string.
  if (firstDot < 2) return false;
  if (firstDot === s.length - 1) return false;
  const main = s.slice(0, firstDot);
  const rest = s.slice(firstDot + 1);
  if (main.length > 63) return false;
  if (!isAsciiAlnumLower(main.charCodeAt(0))) return false;
  if (!isAsciiAlnumLower(main.charCodeAt(main.length - 1))) return false;
  for (let i = 1; i < main.length - 1; i++) {
    const c = main.charCodeAt(i);
    if (!isAsciiAlnumLower(c) && c !== 45 /* '-' */) return false;
  }
  // Suffix: `<seg>(\.<seg>)*`, each seg 2+ pure-alpha, no empty segments.
  if (rest.includes('..') || rest.startsWith('.') || rest.endsWith('.')) return false;
  const segments = rest.split('.');
  for (const seg of segments) {
    if (seg.length < 2) return false;
    for (let i = 0; i < seg.length; i++) {
      if (!isAsciiAlphaLower(seg.charCodeAt(i))) return false;
    }
  }
  return true;
}

/** ASCII `[a-z0-9]` (48-57, 97-122). Caller lowercases the input. */
function isAsciiAlnumLower(c: number): boolean {
  return (c >= 48 && c <= 57) || (c >= 97 && c <= 122);
}

/** ASCII `[a-z]` (97-122). Caller lowercases the input. */
function isAsciiAlphaLower(c: number): boolean {
  return c >= 97 && c <= 122;
}

const EMAIL_QTY_MAX = 20;
const DOMAIN_QTY_MAX = 10;
const DEVICE_QTY_MAX = 50;

const SCAN_FETCH_MS = 15_000;
const PRICES_FETCH_MS = 5_000;
const SCAN_COOLDOWN_MS = 3_000;

type LinePrice = { amount: number; currency: string };

type PublicPrices = Record<'email' | 'domain' | 'laptop', LinePrice | null>;

/** Fallback if API fails or times out (USD estimates only). */
const USD_FALLBACK_PRICES: PublicPrices = {
  email: { amount: 2, currency: 'USD' },
  domain: { amount: 5, currency: 'USD' },
  laptop: { amount: 5, currency: 'USD' },
};

/**
 * Strict client-side normalization — invalid input never reaches the API.
 * Plain text only; never interpreted as HTML.
 */
function sanitizeDomainInputClient(raw: string): { ok: true; domain: string } | { ok: false; error: string } {
  let s = raw.trim().toLowerCase();
  if (s.length > DOMAIN_MAX_LEN) return { ok: false, error: FRIENDLY_DOMAIN_ERROR };
  s = s.replace(/^https?:\/\//, '');
  const hostPart = s.split('/')[0]?.split('?')[0]?.split('#')[0] ?? '';
  s = hostPart.split(':')[0] ?? '';
  s = s.replace(/[^a-zA-Z0-9.-]/g, '').toLowerCase();
  if (s.length > DOMAIN_MAX_LEN) return { ok: false, error: FRIENDLY_DOMAIN_ERROR };
  const scriptScheme = ['java', 'script', ':'].join('');
  if (s.includes(scriptScheme) || s.startsWith(['java', 'script'].join(''))) {
    return { ok: false, error: FRIENDLY_DOMAIN_ERROR };
  }
  while (s.startsWith('www.')) s = s.slice(4);
  if (!s) return { ok: false, error: FRIENDLY_DOMAIN_ERROR };
  if (!isValidDomainShape(s)) return { ok: false, error: FRIENDLY_DOMAIN_ERROR };
  return { ok: true, domain: s };
}

/** Only show server-returned hostnames that pass the same strict shape. */
function safeDisplayDomainFromApi(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const parsed = sanitizeDomainInputClient(raw);
  return parsed.ok ? parsed.domain : null;
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function scrollToDomainScan(): void {
  document.getElementById('domain-scan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function scrollToDomainAndFocus(): void {
  scrollToDomainScan();
  requestAnimationFrame(() => {
    const el = document.getElementById('domain-scan-input');
    if (el && typeof (el as HTMLInputElement).focus === 'function') {
      (el as HTMLInputElement).focus();
    }
  });
}

function scrollToHowItWorks(e: React.MouseEvent<HTMLAnchorElement>): void {
  e.preventDefault();
  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

async function fetchWithTimeout(url: string, ms: number, init?: RequestInit): Promise<Response> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

type DomainScanJson =
  | { domain: string; threatCount: number; pulseCount: number; clean: boolean }
  | { error: string };

function parseDomainScanJson(json: unknown): DomainScanJson | null {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return null;
  const o = json as Record<string, unknown>;
  if (typeof o.error === 'string') return { error: o.error };
  const domain = o.domain;
  const threatCount = o.threatCount;
  const pulseCount = o.pulseCount;
  const clean = o.clean;
  if (
    typeof domain === 'string' &&
    typeof threatCount === 'number' &&
    Number.isFinite(threatCount) &&
    typeof pulseCount === 'number' &&
    Number.isFinite(pulseCount) &&
    typeof clean === 'boolean'
  ) {
    return { domain, threatCount, pulseCount, clean };
  }
  return null;
}

const HOW_STEPS = [
  {
    n: '\u2460',
    title: 'Create your account',
    body: 'Sign up with your email. No credit card required. Tell us about your business — takes under 60 seconds.',
  },
  {
    n: '\u2461',
    title: 'Add what to protect',
    body: 'Add your emails, domain, and devices. Your first asset is monitored free for 30 days.',
  },
  {
    n: '\u2462',
    title: 'We watch it 24/7',
    body: 'Real-time monitoring starts immediately. Get alerted the moment we detect anything suspicious.',
  },
] as const;

function parsePricesPayload(data: unknown): PublicPrices | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const o = data as Record<string, unknown>;
  const arr = o.prices;
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const byType: Partial<Record<keyof PublicPrices, LinePrice>> = {};
  for (const item of arr) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const r = item as Record<string, unknown>;
    const asset_type = r.asset_type;
    const amount = r.amount;
    const currency = r.currency;
    const interval = r.interval;
    if (typeof asset_type !== 'string') continue;
    if (asset_type !== 'email' && asset_type !== 'domain' && asset_type !== 'laptop') continue;
    if (interval !== 'monthly') continue;
    if (typeof currency !== 'string' || !/^[A-Z]{3}$/i.test(currency)) continue;
    if (amount === null || typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) continue;
    byType[asset_type as keyof PublicPrices] = { amount, currency: currency.toUpperCase() };
  }
  const out: PublicPrices = {
    email: byType.email ?? null,
    domain: byType.domain ?? null,
    laptop: byType.laptop ?? null,
  };
  if (!out.email && !out.domain && !out.laptop) return null;
  return {
    email: out.email ?? USD_FALLBACK_PRICES.email,
    domain: out.domain ?? USD_FALLBACK_PRICES.domain,
    laptop: out.laptop ?? USD_FALLBACK_PRICES.laptop,
  };
}

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  /** ISO country for pricing API only — never shown in UI. */
  const [pricingRegionCode, setPricingRegionCode] = useState('US');

  const [domainInput, setDomainInput] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [scanCooldown, setScanCooldown] = useState(false);
  const cooldownTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  const [scanError, setScanError] = useState<string | null>(null);
  const [scanClean, setScanClean] = useState<{ domain: string } | null>(null);
  const [scanRisk, setScanRisk] = useState<{ domain: string } | null>(null);
  const [scanNeutral, setScanNeutral] = useState(false);

  const [publicPrices, setPublicPrices] = useState<PublicPrices | null>(null);
  const [pricesLoadFailed, setPricesLoadFailed] = useState(false);

  const [emailQty, setEmailQty] = useState(1);
  const [domainQty, setDomainQty] = useState(1);
  const [deviceQty, setDeviceQty] = useState(1);

  const beginScanCooldown = useCallback(() => {
    if (cooldownTimerRef.current) window.clearTimeout(cooldownTimerRef.current);
    setScanCooldown(true);
    cooldownTimerRef.current = globalThis.setTimeout(() => {
      setScanCooldown(false);
      cooldownTimerRef.current = null;
    }, SCAN_COOLDOWN_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) window.clearTimeout(cooldownTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithTimeout('https://ipapi.co/json/', 8_000);
        if (!res.ok) throw new Error('geo failed');
        const data = (await res.json()) as { country_code?: string };
        if (cancelled) return;
        const cc = (data.country_code || 'US').toUpperCase();
        setPricingRegionCode(/^[A-Z]{2}$/.test(cc) ? cc : 'US');
      } catch {
        if (!cancelled) setPricingRegionCode('US');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const cc = (pricingRegionCode || 'US').toUpperCase();
    const qs = new URLSearchParams({
      country_code: /^[A-Z]{2}$/.test(cc) ? cc : 'US',
      interval: 'monthly',
    });
    const url = `${API_BASE}/api/public/asset-prices?${qs.toString()}`;
    (async () => {
      try {
        const res = await fetchWithTimeout(url, PRICES_FETCH_MS, { method: 'GET' });
        if (cancelled) return;
        if (!res.ok) {
          setPublicPrices(USD_FALLBACK_PRICES);
          setPricesLoadFailed(true);
          return;
        }
        let data: unknown;
        try {
          data = await res.json();
        } catch {
          setPublicPrices(USD_FALLBACK_PRICES);
          setPricesLoadFailed(true);
          return;
        }
        const parsed = parsePricesPayload(data);
        if (cancelled) return;
        if (!parsed) {
          setPublicPrices(USD_FALLBACK_PRICES);
          setPricesLoadFailed(true);
        } else {
          setPublicPrices(parsed);
          setPricesLoadFailed(false);
        }
      } catch {
        if (!cancelled) {
          setPublicPrices(USD_FALLBACK_PRICES);
          setPricesLoadFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pricingRegionCode]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  const effectivePrices = publicPrices ?? USD_FALLBACK_PRICES;

  const displayCurrency = useMemo(() => {
    return (
      effectivePrices.email?.currency ||
      effectivePrices.domain?.currency ||
      effectivePrices.laptop?.currency ||
      'USD'
    );
  }, [effectivePrices]);

  const emailSubtotal = useMemo(() => {
    const p = effectivePrices.email;
    if (!p) return 0;
    return p.amount * emailQty;
  }, [effectivePrices.email, emailQty]);

  const domainSubtotal = useMemo(() => {
    const p = effectivePrices.domain;
    if (!p) return 0;
    return p.amount * domainQty;
  }, [effectivePrices.domain, domainQty]);

  const deviceSubtotal = useMemo(() => {
    const p = effectivePrices.laptop;
    if (!p) return 0;
    return p.amount * deviceQty;
  }, [effectivePrices.laptop, deviceQty]);

  const monthlyEstimate = useMemo(
    () => emailSubtotal + domainSubtotal + deviceSubtotal,
    [emailSubtotal, domainSubtotal, deviceSubtotal]
  );

  const runDomainScan = useCallback(async () => {
    setScanError(null);
    setScanClean(null);
    setScanRisk(null);
    setScanNeutral(false);

    const parsed = sanitizeDomainInputClient(domainInput);
    if (!parsed.ok) {
      setScanError(FRIENDLY_DOMAIN_ERROR);
      return;
    }

    setScanLoading(true);
    try {
      const qs = new URLSearchParams({ domain: parsed.domain });
      const url = `${API_BASE}/api/domain/scan?${qs.toString()}`;
      let res: Response;
      try {
        res = await fetchWithTimeout(url, SCAN_FETCH_MS, { method: 'GET' });
      } catch {
        setScanNeutral(true);
        return;
      }

      let json: unknown;
      try {
        json = await res.json();
      } catch {
        setScanNeutral(true);
        return;
      }

      const body = parseDomainScanJson(json);
      if (!body || !res.ok) {
        setScanNeutral(true);
        return;
      }
      if ('error' in body) {
        setScanNeutral(true);
        return;
      }

      const safeDomain = safeDisplayDomainFromApi(body.domain);
      if (!safeDomain) {
        setScanNeutral(true);
        return;
      }

      if (body.clean) {
        setScanClean({ domain: safeDomain });
      } else {
        setScanRisk({ domain: safeDomain });
      }
    } catch {
      setScanNeutral(true);
    } finally {
      setScanLoading(false);
      beginScanCooldown();
    }
  }, [domainInput, beginScanCooldown]);

  const bump = (setter: React.Dispatch<React.SetStateAction<number>>, delta: number, current: number, max: number) => {
    setter(() => Math.min(max, Math.max(1, current + delta)));
  };

  const scanButtonDisabled = scanLoading || scanCooldown;

  return (
    <main className="overflow-x-hidden bg-navy text-foreground">
      <header
        className={`fixed top-0 z-50 w-full border-b transition-[background-color,backdrop-filter,border-color] duration-300 ${
          scrolled
            ? 'border-border/60 bg-navy/90 backdrop-blur-md'
            : 'border-transparent bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:h-[4.25rem] sm:px-6 lg:px-8">
          <div className="text-lg sm:text-xl">
            <Logo size="md" onNavigate={closeMobile} />
          </div>

          <nav className="hidden items-center gap-3 md:flex" aria-label="Primary">
            <span className="text-sm text-muted">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-accent underline-offset-4 hover:underline">
                Sign in
              </Link>
            </span>
            <ThemeToggle />
            <Link
              to="/signup"
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-4 py-2 text-sm font-bold text-cta-text transition hover:bg-accent/90"
            >
              Start free →
            </Link>
          </nav>

          <div className="flex items-center gap-2 md:hidden">
            <ThemeToggle />
            <button
              type="button"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-border p-2 text-foreground"
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div
          id="mobile-nav"
          className={`md:hidden ${mobileOpen ? 'block' : 'hidden'} border-t border-border/60 bg-navy/95 backdrop-blur-md`}
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-4 sm:px-6">
            <p className="text-center text-sm text-muted">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-accent underline-offset-4 hover:underline" onClick={closeMobile}>
                Sign in
              </Link>
            </p>
            <Link
              to="/signup"
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-4 py-3 text-center font-bold text-cta-text hover:bg-accent/90"
              onClick={closeMobile}
            >
              Start free →
            </Link>
          </div>
        </div>
      </header>

      {/* Section 1 — Hero */}
      <section className="relative w-full px-4 pb-20 pt-28 sm:px-6 sm:pb-24 sm:pt-32 lg:px-8 lg:pb-28 lg:pt-36">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_95%_65%_at_50%_-15%,color-mix(in_srgb,var(--accent-green)_22%,transparent),transparent)]" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/45 to-transparent" />
        <div className="relative mx-auto max-w-5xl text-center">
          <p className="text-[0.65rem] font-bold uppercase tracking-[0.28em] text-accent sm:text-xs">
            AI-Powered Cybersecurity for Every Business
          </p>
          <h1 className="mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-[3.35rem]">
            We Stop Threats.
            <br />
            Before They Stop You.
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-base leading-snug text-muted sm:text-lg md:text-xl md:leading-snug">
            ShieldSME monitors your emails, domains, and devices 24/7 for cyber threats — and tells you exactly what to
            do, in plain English. No IT team needed.
          </p>

          <div
            id="domain-scan"
            className="mx-auto mt-12 max-w-3xl scroll-mt-28 rounded-2xl border border-border/80 bg-surface/95 p-5 shadow-2xl ring-1 ring-white/5 backdrop-blur-sm sm:p-6"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
              <span className="hidden shrink-0 text-lg sm:inline" aria-hidden>
                {'\u{1F50D}'}
              </span>
              <input
                id="domain-scan-input"
                name="domain"
                type="text"
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                maxLength={DOMAIN_MAX_LEN}
                placeholder="yourbusiness.com"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                className="min-h-[52px] w-full flex-1 rounded-xl border border-border bg-navy/90 px-4 py-3 text-base text-foreground placeholder:text-muted/60 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25"
              />
              <button
                type="button"
                disabled={scanButtonDisabled}
                onClick={() => void runDomainScan()}
                className="inline-flex min-h-[52px] min-w-[160px] shrink-0 items-center justify-center gap-2 rounded-xl bg-accent px-6 py-3 text-sm font-bold text-cta-text transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {scanLoading ? (
                  <>
                    <Spinner className="h-5 w-5 text-cta-text" />
                    Scanning…
                  </>
                ) : (
                  'Scan free →'
                )}
              </button>
            </div>
            <p className="mt-3 text-center text-xs text-muted sm:text-sm">
              Instant threat scan · No account needed · Free forever
            </p>

            {scanError ? (
              <p className="mt-4 rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-center text-sm text-foreground">
                {scanError}
              </p>
            ) : null}

            {scanClean ? (
              <div className="mt-4 rounded-xl border border-emerald-500/45 bg-emerald-500/10 px-4 py-4 text-left text-sm sm:text-base">
                <p className="font-semibold text-foreground">
                  {'\u2705'} No active threats detected for {scanClean.domain}
                </p>
                <p className="mt-3">
                  <Link to="/signup" className="font-medium text-accent underline-offset-4 hover:underline">
                    Monitor this domain continuously →
                  </Link>
                </p>
              </div>
            ) : null}

            {scanRisk ? (
              <div className="mt-4 rounded-xl border border-amber-500/50 bg-amber-500/10 px-4 py-4 text-left text-sm sm:text-base">
                <p className="font-semibold text-foreground">
                  {'\u26A0\uFE0F'} We found potential security risks for {scanRisk.domain}
                </p>
                <p className="mt-3">
                  <Link to="/signup" className="font-medium text-accent underline-offset-4 hover:underline">
                    Create a free account to see the full report →
                  </Link>
                </p>
              </div>
            ) : null}

            {scanNeutral ? (
              <div className="mt-4 rounded-xl border border-border bg-surface/80 px-4 py-4 text-left text-sm text-muted sm:text-base">
                <p>Scan complete. Create a free account for detailed results →</p>
                <p className="mt-3">
                  <Link to="/signup" className="font-medium text-accent underline-offset-4 hover:underline">
                    Create free account →
                  </Link>
                </p>
              </div>
            ) : null}
          </div>

          <div className="mt-12 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center sm:gap-4">
            <Link
              to="/signup"
              className="inline-flex min-h-[52px] items-center justify-center rounded-xl bg-accent px-8 py-3.5 text-center text-base font-bold text-cta-text shadow-lg shadow-accent/25 transition hover:bg-accent/90"
            >
              Start protecting free →
            </Link>
            <a
              href="#how-it-works"
              onClick={scrollToHowItWorks}
              className="inline-flex min-h-[52px] items-center justify-center rounded-xl border border-border/90 bg-navy/50 px-8 py-3.5 text-center text-base font-semibold text-foreground backdrop-blur-sm transition hover:bg-foreground/5"
            >
              See how it works ↓
            </a>
          </div>
          <p className="mt-10 text-sm text-muted sm:text-base">
            No credit card required · First asset free for 30 days · Cancel anytime
          </p>
          <p className="mx-auto mt-3 max-w-xl text-xs text-muted/90 sm:text-sm">
            Prices are estimates. Final price confirmed before payment.
          </p>
        </div>
      </section>

      {/* Section 2 — Stats */}
      <section className="border-y border-border/60 bg-[#050a10] px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto grid max-w-5xl gap-12 sm:grid-cols-3 sm:gap-8">
          {[
            { k: '24/7', t: 'Continuous monitoring, never sleeps' },
            { k: 'Plain English', t: 'No jargon. Just clear steps to stay safe.' },
            { k: 'Minutes', t: 'From signup to active monitoring' },
          ].map((s) => (
            <div key={s.k} className="text-center">
              <p className="text-2xl font-extrabold tracking-tight text-accent sm:text-3xl">{s.k}</p>
              <p className="mt-3 text-sm leading-relaxed text-muted sm:text-base">{s.t}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Section 3 — What we protect */}
      <section
        id="features"
        className="scroll-mt-24 border-t border-border/80 bg-section-dim px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24"
      >
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl md:text-[2.5rem]">
            Everything your business needs protected
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted sm:text-lg">
            One platform. Every threat. Zero confusion.
          </p>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: '\u{1F4E7}',
                title: 'Email Breach Monitoring',
                body: "We scan global breach databases for your business emails. If your data is exposed, you'll know within hours — not months.",
              },
              {
                icon: '\u{1F310}',
                title: 'Domain Threat Intelligence',
                body: 'We monitor global threat feeds for your domain daily. Phishing sites, DNS attacks, and reputation risks — caught early.',
              },
              {
                icon: '\u{1F4BB}',
                title: 'Endpoint Security',
                body: 'Our lightweight agent monitors your devices around the clock. Suspicious processes, unusual connections — detected in real time.',
              },
              {
                icon: '\u{1F512}',
                title: 'Security Health Score',
                body: 'Your personal security scorecard, updated in real time. Know exactly where you stand and what needs your attention first.',
              },
              {
                icon: '\u{1F4F2}',
                title: 'Mobile Money Fraud Protection',
                body: "AI-powered detection for mobile payment scams. Paste any suspicious message and know instantly if it's fraud.",
                badge: 'Free · Available in supported regions',
              },
              {
                icon: '\u{1F916}',
                title: 'AI Security Briefs',
                body: 'A weekly plain-English summary of your security status. Written by AI, tailored to your business, delivered automatically.',
              },
            ].map((card) => (
              <div
                key={card.title}
                className="flex h-full flex-col rounded-2xl border border-border bg-surface p-6 shadow-sm transition hover:border-accent/40 hover:shadow-md sm:p-7"
              >
                <div className="flex flex-wrap items-start gap-2">
                  <span className="text-2xl leading-none" aria-hidden>
                    {card.icon}
                  </span>
                  <h3 className="text-lg font-bold text-foreground">{card.title}</h3>
                </div>
                {'badge' in card && card.badge ? (
                  <span className="mt-2 inline-flex w-fit rounded-full bg-accent/15 px-2.5 py-1 text-xs font-semibold text-accent">
                    {card.badge}
                  </span>
                ) : null}
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted sm:text-base">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4 — How it works */}
      <section
        id="how-it-works"
        className="scroll-mt-24 border-t border-border/80 bg-[#050a10] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24"
      >
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Up and running in minutes
          </h2>

          <div className="mt-14 hidden items-stretch justify-center gap-0 lg:flex">
            {HOW_STEPS.map((step, i) => (
              <React.Fragment key={step.title}>
                {i > 0 ? (
                  <div className="flex w-10 shrink-0 items-center justify-center" aria-hidden>
                    <div className="h-px w-full bg-accent/35" />
                  </div>
                ) : null}
                <div className="max-w-[17rem] flex-1 text-center">
                  <span className="text-2xl font-bold text-accent">{step.n}</span>
                  <h3 className="mt-3 text-lg font-bold text-foreground">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted">{step.body}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-10 flex flex-col gap-10 lg:hidden">
            {HOW_STEPS.map((step) => (
              <div key={step.title} className="border-l-2 border-accent/40 pl-5 text-left">
                <span className="text-2xl font-bold text-accent">{step.n}</span>
                <h3 className="mt-2 text-lg font-bold text-foreground">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 — Pricing */}
      <section
        id="pricing"
        className="scroll-mt-24 border-t border-border/80 bg-section-dim px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24"
      >
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Pay only for what you protect
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-muted sm:text-lg">
            Start free. Add assets as your business grows.
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-center text-xs leading-relaxed text-muted sm:text-sm">
            Prices shown are estimates based on your location and subject to change. Final price is confirmed before any
            payment is taken.
          </p>
          <p className="mx-auto mt-2 text-center text-xs text-muted/90 sm:text-sm">
            Prices are estimates. Final price confirmed before payment.
          </p>
          {pricesLoadFailed ? (
            <p className="mt-3 text-center text-xs text-muted">Showing USD estimates while we load localized pricing.</p>
          ) : null}

          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            <div className="flex h-full flex-col rounded-2xl border border-border bg-surface p-6 shadow-sm transition hover:border-accent/35 sm:p-7">
              <p className="text-lg font-bold text-foreground">
                <span aria-hidden>{'\u{1F4E7}'}</span> Email Monitoring
              </p>
              <p className="mt-2 text-2xl font-extrabold text-accent">
                {effectivePrices.email
                  ? `${formatMoney(effectivePrices.email.amount, effectivePrices.email.currency)}/mo`
                  : '—'}
              </p>
              <p className="mt-1 text-sm text-muted">per email address</p>
              <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-muted">Quantity</p>
              <div className="mt-2 flex items-center justify-center gap-4 rounded-xl border border-border/70 bg-navy/40 py-2">
                <button
                  type="button"
                  onClick={() => bump(setEmailQty, -1, emailQty, EMAIL_QTY_MAX)}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-lg font-bold transition hover:bg-foreground/5"
                  aria-label="Decrease email quantity"
                >
                  −
                </button>
                <span className="min-w-[2ch] text-center text-lg font-bold">{emailQty}</span>
                <button
                  type="button"
                  onClick={() => bump(setEmailQty, 1, emailQty, EMAIL_QTY_MAX)}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-lg font-bold transition hover:bg-foreground/5"
                  aria-label="Increase email quantity"
                >
                  +
                </button>
              </div>
              <p className="mt-4 text-sm text-muted">
                Subtotal:{' '}
                <span className="font-semibold text-foreground">
                  {effectivePrices.email ? formatMoney(emailSubtotal, effectivePrices.email.currency) : '—'}
                </span>
              </p>
            </div>

            <div className="flex h-full flex-col rounded-2xl border border-border bg-surface p-6 shadow-sm transition hover:border-accent/35 sm:p-7">
              <p className="text-lg font-bold text-foreground">
                <span aria-hidden>{'\u{1F310}'}</span> Domain Security
              </p>
              <p className="mt-2 text-2xl font-extrabold text-accent">
                {effectivePrices.domain
                  ? `${formatMoney(effectivePrices.domain.amount, effectivePrices.domain.currency)}/mo`
                  : '—'}
              </p>
              <p className="mt-1 text-sm text-muted">per domain</p>
              <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-muted">Quantity</p>
              <div className="mt-2 flex items-center justify-center gap-4 rounded-xl border border-border/70 bg-navy/40 py-2">
                <button
                  type="button"
                  onClick={() => bump(setDomainQty, -1, domainQty, DOMAIN_QTY_MAX)}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-lg font-bold transition hover:bg-foreground/5"
                  aria-label="Decrease domain quantity"
                >
                  −
                </button>
                <span className="min-w-[2ch] text-center text-lg font-bold">{domainQty}</span>
                <button
                  type="button"
                  onClick={() => bump(setDomainQty, 1, domainQty, DOMAIN_QTY_MAX)}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-lg font-bold transition hover:bg-foreground/5"
                  aria-label="Increase domain quantity"
                >
                  +
                </button>
              </div>
              <p className="mt-4 text-sm text-muted">
                Subtotal:{' '}
                <span className="font-semibold text-foreground">
                  {effectivePrices.domain ? formatMoney(domainSubtotal, effectivePrices.domain.currency) : '—'}
                </span>
              </p>
            </div>

            <div className="flex h-full flex-col rounded-2xl border border-border bg-surface p-6 shadow-sm transition hover:border-accent/35 sm:p-7">
              <p className="text-lg font-bold text-foreground">
                <span aria-hidden>{'\u{1F4BB}'}</span> Device Monitoring
              </p>
              <p className="mt-2 text-2xl font-extrabold text-accent">
                {effectivePrices.laptop
                  ? `${formatMoney(effectivePrices.laptop.amount, effectivePrices.laptop.currency)}/mo`
                  : '—'}
              </p>
              <p className="mt-1 text-sm text-muted">per device</p>
              <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-muted">Quantity</p>
              <div className="mt-2 flex items-center justify-center gap-4 rounded-xl border border-border/70 bg-navy/40 py-2">
                <button
                  type="button"
                  onClick={() => bump(setDeviceQty, -1, deviceQty, DEVICE_QTY_MAX)}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-lg font-bold transition hover:bg-foreground/5"
                  aria-label="Decrease device quantity"
                >
                  −
                </button>
                <span className="min-w-[2ch] text-center text-lg font-bold">{deviceQty}</span>
                <button
                  type="button"
                  onClick={() => bump(setDeviceQty, 1, deviceQty, DEVICE_QTY_MAX)}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-border text-lg font-bold transition hover:bg-foreground/5"
                  aria-label="Increase device quantity"
                >
                  +
                </button>
              </div>
              <p className="mt-4 text-sm text-muted">
                Subtotal:{' '}
                <span className="font-semibold text-foreground">
                  {effectivePrices.laptop ? formatMoney(deviceSubtotal, effectivePrices.laptop.currency) : '—'}
                </span>
              </p>
            </div>

            <div className="flex h-full flex-col rounded-2xl border border-accent/35 bg-surface p-6 shadow-sm sm:p-7">
              <p className="text-lg font-bold text-foreground">
                <span aria-hidden>{'\u{1F4F2}'}</span> Mobile Money Protection
              </p>
              <p className="mt-2 text-2xl font-extrabold text-accent">FREE</p>
              <p className="mt-1 text-sm text-muted">Always included in supported regions</p>
              <span className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-accent/15 px-3 py-1.5 text-xs font-bold text-accent">
                ✓ Included
              </span>
            </div>
          </div>

          <div className="mx-auto mt-12 max-w-xl rounded-2xl border border-border bg-surface p-6 text-center shadow-md sm:p-8">
            <p className="text-sm font-semibold uppercase tracking-wide text-muted">Estimated monthly cost</p>
            <p className="mt-2 text-3xl font-extrabold text-foreground">
              {formatMoney(monthlyEstimate, displayCurrency)}
              <span className="text-lg font-semibold text-muted"> /month</span>
            </p>
            <p className="mt-4 text-sm text-muted">First asset free for 30 days</p>
            <Link
              to="/signup"
              className="mt-8 inline-flex min-h-[48px] w-full items-center justify-center rounded-xl bg-accent px-6 py-3 text-base font-bold text-cta-text transition hover:bg-accent/90 sm:w-auto sm:min-w-[240px]"
            >
              Start monitoring free →
            </Link>
            <p className="mt-4 text-xs text-muted sm:text-sm">Prices shown in your local currency · Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* Section 6 — Trust */}
      <section className="border-t border-border/80 bg-[#050a10] px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Built on enterprise-grade security
          </h2>
          <ul className="mx-auto mt-12 grid gap-6 sm:grid-cols-2">
            {[
              {
                icon: '\u{1F512}',
                title: 'Zero Trust Architecture',
                body: 'Every request verified. No implicit trust, anywhere.',
              },
              {
                icon: '\u{1F6E1}\uFE0F',
                title: 'End-to-End Encrypted',
                body: 'Your data is encrypted in transit and at rest, always.',
              },
              {
                icon: '\u{1F4CB}',
                title: 'Compliance Ready',
                body: 'Designed to support local and international data protection regulations wherever your business operates.',
              },
              {
                icon: '\u{1F916}',
                title: 'AI-Powered Detection',
                body: 'Real-time threat intelligence from global security databases, updated continuously to catch emerging threats.',
              },
            ].map((item) => (
              <li key={item.title} className="flex gap-4 rounded-2xl border border-border/80 bg-navy/40 p-5 sm:p-6">
                <span className="text-2xl" aria-hidden>
                  {item.icon}
                </span>
                <div>
                  <p className="font-bold text-foreground">{item.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted sm:text-base">{item.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 7 — Final CTA */}
      <section className="relative border-t border-border/80 px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_55%_at_50%_110%,color-mix(in_srgb,var(--accent-green)_16%,transparent),transparent)]" />
        <div className="relative mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Start protecting your business today
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-muted sm:text-lg">
            Join businesses worldwide using ShieldSME to stay one step ahead of cyber threats.
          </p>
          <div className="mt-10 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:justify-center">
            <Link
              to="/signup"
              className="inline-flex min-h-[52px] items-center justify-center rounded-xl bg-accent px-8 py-3.5 text-base font-bold text-cta-text transition hover:bg-accent/90"
            >
              Create free account →
            </Link>
            <button
              type="button"
              onClick={scrollToDomainAndFocus}
              className="inline-flex min-h-[52px] items-center justify-center rounded-xl border border-border bg-navy/50 px-8 py-3.5 text-base font-semibold text-foreground backdrop-blur-sm transition hover:bg-foreground/5"
            >
              Scan your domain free →
            </button>
          </div>
          <p className="mt-10 text-sm text-muted">
            No credit card required · First asset free for 30 days · Cancel anytime
          </p>
        </div>
      </section>

      <MarketingFooter />
    </main>
  );
}
